import React, { useEffect, useState } from 'react';
import { ConnectButton } from '@/components/navigation/ConnectButton';
import { LoadingFallback } from '@/components/common/LoadingFallback';
import { ConstellationLoader } from '@/components/common/ConstellationLoader';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'hana-viewer': {
        url: string;
        style?: React.CSSProperties;
        loading?: string;
        onLoad?: () => void;
        onError?: () => void;
      };
    }
  }
}

const SplineLandingPage = () => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isSceneLoaded, setIsSceneLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [showCustomLoader, setShowCustomLoader] = useState(true);

  useEffect(() => {
    const loadScript = async () => {
      try {
        // Check if script is already loaded
        if (document.querySelector('script[src*="hana-viewer"]')) {
          setIsScriptLoaded(true);
          return;
        }

        const script = document.createElement('script');
        script.type = 'module';
        script.src = 'https://cdn.spline.design/@splinetool/hana-viewer@1.0.75/hana-viewer.js';
        
        script.onload = () => setIsScriptLoaded(true);
        script.onerror = () => setLoadError(true);
        
        document.head.appendChild(script);
      } catch (error) {
        console.error('Failed to load Hana viewer script:', error);
        setLoadError(true);
      }
    };

    loadScript();
  }, []);

  // Use a timeout to assume scene has loaded after script loads
  useEffect(() => {
    if (isScriptLoaded && !isSceneLoaded) {
      const timer = setTimeout(() => {
        setIsSceneLoaded(true);
        // Hide custom loader after scene loads with a small delay for smooth transition
        setTimeout(() => setShowCustomLoader(false), 800);
      }, 2000); // Give 2 seconds for scene to load after script

      return () => clearTimeout(timer);
    }
  }, [isScriptLoaded, isSceneLoaded]);

  if (loadError) {
    return (
      <div className="relative w-full h-screen overflow-hidden bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Unable to load 3D scene</p>
          <div className="absolute inset-0 flex items-end justify-center pb-12 z-10">
            <ConnectButton />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Constellation Loading Animation - Always render, control with opacity */}
      <ConstellationLoader isVisible={showCustomLoader} />

      {/* Hana Background */}
      <hana-viewer 
        url="https://prod.spline.design/EMTaj3OjlBUxef1F-t8z/scene.hanacode"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          opacity: isSceneLoaded ? 1 : 0,
          transition: 'opacity 0.8s ease-in-out'
        }}
        loading="eager"
      />
      
      {/* Login Button Overlay */}
      <div 
        className="absolute inset-0 flex items-end justify-center pb-12 z-40"
        style={{ pointerEvents: 'none' }}
      >
        <div 
          className={`transition-opacity duration-800 ${isSceneLoaded ? 'opacity-100' : 'opacity-50'}`}
          style={{ pointerEvents: 'auto' }}
        >
          <ConnectButton />
        </div>
      </div>
    </div>
  );
};

export default SplineLandingPage;